import React from 'react';
import { motion } from 'framer-motion';
import { responsiveCellSize } from '../utils/responsiveCellSize';
import { visualColors } from '../colors';
interface GeoGridProps {
  width: number;
  height: number;
  depth?: number;
  mode?: 'stimulus' | 'support' | 'perimeter' | 'area';
  label?: string;
  highlighted?: number;
  showGrid?: boolean;
  unit?: string;
}
export function GeoGrid({
  width,
  height,
  depth,
  mode = 'area',
  label,
  highlighted,
  showGrid = true,
  unit
}: GeoGridProps) {
  const padding = 40;
  // Dynamic cell size: scale down for large dimensions to fit max ~500px
  const maxSvgDim = 460; // max pixel dimension for the grid area
  const rawCellSize = 20;
  const cellWidth = responsiveCellSize(width, maxSvgDim, rawCellSize, 4);
  const cellHeight = responsiveCellSize(height, maxSvgDim, rawCellSize, 4);
  const cellSize = Math.min(cellWidth, cellHeight);
  // For very large grids (>20 cells), disable individual cell rendering and show a solid rect
  const tooManyGridCells = width * height > 400;
  const effectiveShowGrid = showGrid && !tooManyGridCells;
  // Determine display mode
  const isStimulus = mode === 'stimulus';
  const isPerimeter = mode === 'perimeter';
  const isArea = mode === 'area' || mode === 'support';
  const showCalculation = !isStimulus;
  const unitLabel = unit ? ` ${unit}` : '';
  const svgWidth = Math.round(width * cellSize + padding * 2);
  const svgHeight = Math.round(height * cellSize + padding * 2);
  return <div className="bg-slate-50 p-6 rounded-xl border border-slate-100" data-id="element-12127">
      {label && <div className="text-sm font-medium text-slate-700 mb-3 text-center" data-id="element-12128">
          {label}
        </div>}
      <div className="flex justify-center overflow-hidden" data-id="element-12129">
        <svg width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="max-w-full h-auto" data-id="element-12130">
          {/* Grid cells */}
          {effectiveShowGrid && Array.from({
          length: height
        }).map((_, row) => Array.from({
          length: width
        }).map((_, col) => {
          const cellIndex = row * width + col;
          const hasShading = highlighted !== undefined && highlighted > 0;
          const isShaded = hasShading && cellIndex < highlighted;
          const cellFill = isPerimeter ? 'white' : hasShading ? isShaded ? 'rgb(99, 102, 241)' : 'white' : 'rgb(199, 210, 254)';
          return <motion.rect key={`${row}-${col}`} x={padding + col * cellSize} y={padding + row * cellSize} width={cellSize} height={cellSize} fill={cellFill} stroke={visualColors.axisGuide} strokeWidth="1" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: Math.min((row * width + col) * 0.02, 0.5)
          }} data-id="element-12131" />;
        }))}

          {/* Solid rectangle (when grid is off or too many cells) */}
          {(!showGrid || tooManyGridCells) && <motion.rect x={padding} y={padding} width={width * cellSize} height={height * cellSize} fill="rgb(199, 210, 254)" stroke={visualColors.axisGuide} strokeWidth="2" rx="4" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} data-id="element-12132" />}

          {/* Perimeter highlight */}
          {isPerimeter && <motion.rect x={padding} y={padding} width={width * cellSize} height={height * cellSize} fill="none" stroke="rgb(99, 102, 241)" strokeWidth="4" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 1.5,
          ease: 'easeInOut'
        }} data-id="element-12133" />}

          {/* Width dimension (top) */}
          <text x={padding + width * cellSize / 2} y={padding - 10} textAnchor="middle" className="text-sm font-bold fill-slate-700" data-id="element-12134">
            {width}
            {unitLabel}
          </text>

          {/* Height dimension (left) */}
          <text x={padding - 10} y={padding + height * cellSize / 2} textAnchor="end" dominantBaseline="middle" className="text-sm font-bold fill-slate-700" data-id="element-12135">
            {height}
            {unitLabel}
          </text>

          {/* Depth dimension */}
          {depth && <text x={padding + width * cellSize + 10} y={padding + height * cellSize + 15} textAnchor="start" className="text-sm font-bold fill-indigo-600" data-id="element-12136">
              diepte: {depth}
              {unitLabel}
            </text>}
        </svg>
      </div>

      {/* Calculation text */}
      {showCalculation && <div className="mt-4 text-center text-sm text-slate-600" data-id="element-12137">
          {depth ? <p data-id="element-12138">
              Inhoud = {width} × {depth} × {height} = {width * depth * height}
              {unit ? ` ${unit}³` : ''}
            </p> : isPerimeter ? <p data-id="element-12139">
              Omtrek = {width} + {height} + {width} + {height} ={' '}
              {2 * (width + height)}
              {unitLabel && ` ${unit}`}
            </p> : <p data-id="element-12140">
              Oppervlakte = {width} × {height} = {width * height}
              {unitLabel && ` ${unit}²`}
            </p>}
        </div>}
    </div>;
}